import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface Advantage {
  title: string;
  content: string;
}

interface AdvantagesData {
  intro: string;
  advantages: Advantage[];
}

const EditAdvantages = () => {
  // State for storing advantages content
  const [advantagesData, setAdvantagesData] = useState<AdvantagesData>({
    intro: "",
    advantages: [],
  });

  // State for tracking which section is being edited
  const [editingSection, setEditingSection] = useState<string | null>(null);
  // State for tracking which advantage is being edited
  const [editingAdvantageIndex, setEditingAdvantageIndex] = useState<
    number | null
  >(null);
  // State for adding new advantage
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Temporary state for editing
  const [editText, setEditText] = useState("");
  const [editTitle, setEditTitle] = useState("");
  // Loading state
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [advantageToDelete, setAdvantageToDelete] = useState<number | null>(
    null
  );

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchAdvantagesData = async () => {
      try {
        const advantagesRef = ref(db, "LayoutSettingData/Advantages");
        const snapshot = await get(advantagesRef);

        if (snapshot.exists()) {
          setAdvantagesData(snapshot.val());
        } else {
          // If no data exists, initialize with default content
          const initialData: AdvantagesData = {
            intro:
              "At Afrocart, we pride ourselves on offering numerous advantages to enhance your shopping and catering experience. Our platform is designed to cater to your needs with efficiency and excellence.",
            advantages: [
              {
                title: "Convenience",
                content:
                  "With our easy-to-use online platform, you can shop for groceries and catering supplies from the comfort of your home or office. Our streamlined ordering process and fast delivery ensure a hassle-free experience.",
              },
              {
                title: "Quality Assurance",
                content:
                  "We ensure that all products meet high standards of quality. From fresh groceries to catering supplies, we carefully select and vet our products to guarantee the best for our customers.",
              },
              {
                title: "Competitive Pricing",
                content:
                  "Our pricing is designed to be competitive, providing value for money without compromising on quality. Enjoy the best prices on a wide range of products and services.",
              },
              {
                title: "Tailored Services",
                content:
                  "We offer tailored services for catering businesses, ensuring that your specific needs are met. Our platform is equipped to handle bulk orders and customized requests efficiently.",
              },
              {
                title: "Customer Support",
                content:
                  "Our dedicated customer support team is available to assist you with any queries or issues. We are committed to providing prompt and helpful support to enhance your experience with Afrocart.",
              },
            ],
          };
          setAdvantagesData(initialData);
          await saveToFirebase(initialData);
        }
      } catch (error) {
        console.error("Error fetching advantages data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvantagesData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = advantagesData) => {
    try {
      const advantagesRef = ref(db, "LayoutSettingData/Advantages");
      await set(advantagesRef, data);
      toast({
        title: "Success",
        description: "Changes saved successfully",
        variant: "success",
        showIcon: true,
      });
      return true;
    } catch (error) {
      console.error("Error saving advantages data:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
        showIcon: true,
      });
      return false;
    }
  };

  // Handle edit intro button click
  const handleEditIntro = () => {
    setEditingSection("intro");
    setEditText(advantagesData.intro);
  };

  // Handle edit advantage button click
  const handleEditAdvantage = (index: number) => {
    setEditingAdvantageIndex(index);
    setEditTitle(advantagesData.advantages[index].title);
    setEditText(advantagesData.advantages[index].content);
  };

  // Handle add new advantage button click
  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditTitle("");
    setEditText("");
  };

  // Handle delete advantage button click
  const handleDelete = (index: number) => {
    setAdvantageToDelete(index);
    setShowDeleteDialog(true);
  };

  // Handle save intro button click
  const handleSaveIntro = async () => {
    const updatedData = { ...advantagesData, intro: editText };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setAdvantagesData(updatedData);
      setEditingSection(null);
    }
  };

  // Handle save advantage button click
  const handleSaveAdvantage = async () => {
    if (editingAdvantageIndex !== null) {
      const updatedAdvantages = [...advantagesData.advantages];
      updatedAdvantages[editingAdvantageIndex] = {
        title: editTitle,
        content: editText,
      };
      const updatedData = { ...advantagesData, advantages: updatedAdvantages };
      const success = await saveToFirebase(updatedData);
      if (success) {
        setAdvantagesData(updatedData);
        setEditingAdvantageIndex(null);
      }
    }
  };

  // Handle save new advantage button click
  const handleSaveNew = async () => {
    if (editTitle.trim() === "" || editText.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter both title and content",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newAdvantage = {
      title: editTitle,
      content: editText,
    };
    const updatedAdvantages = [...advantagesData.advantages, newAdvantage];
    const updatedData = { ...advantagesData, advantages: updatedAdvantages };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setAdvantagesData(updatedData);
      setIsAddingNew(false);
      setEditTitle("");
      setEditText("");
      toast({
        title: "Success",
        description: "New advantage added successfully",
        variant: "success",
        showIcon: true,
      });
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    setEditingSection(null);
    setEditingAdvantageIndex(null);
    setIsAddingNew(false);
    setEditTitle("");
    setEditText("");
  };

  const confirmDelete = async () => {
    if (advantageToDelete !== null) {
      const updatedAdvantages = [...advantagesData.advantages];
      updatedAdvantages.splice(advantageToDelete, 1);
      const updatedData = { ...advantagesData, advantages: updatedAdvantages };
      const success = await saveToFirebase(updatedData);
      if (success) {
        setAdvantagesData(updatedData);
        toast({
          title: "Success",
          description: "Advantage deleted successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setAdvantageToDelete(null);
  };

  // Render intro section
  const renderIntro = () => {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        {editingSection === "intro" ? (
          <div className="space-y-4">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
            />
            <div className="flex space-x-3">
              <button
                onClick={handleSaveIntro}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <p className="leading-relaxed">{advantagesData.intro}</p>
            <button
              className="text-customGreenColor hover:text-opacity-80 transition-all ml-3 flex-shrink-0"
              onClick={handleEditIntro}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render advantage item
  const renderAdvantage = (advantage: Advantage, index: number) => {
    return (
      <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-sm">
        {editingAdvantageIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleSaveAdvantage}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-helveticaBold text-2xl">{advantage.title}</h2>
              <div className="flex space-x-3">
                <button
                  className="text-customGreenColor hover:text-opacity-80 transition-all"
                  onClick={() => handleEditAdvantage(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500 hover:text-opacity-80 transition-all"
                  onClick={() => handleDelete(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="whitespace-pre-line leading-relaxed">
              {advantage.content}
            </p>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Advantages of Afrocart
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddNew}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Advantage
          </button>
        </div>

        {/* Intro section */}
        {renderIntro()}

        {/* Add New Advantage Form */}
        {isAddingNew && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-xl mb-4">
              Add New Advantage
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter advantage title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter advantage content"
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSaveNew}
                  className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-2" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Advantages List */}
        <div className="grid gap-6">
          {advantagesData.advantages.map((advantage, index) =>
            renderAdvantage(advantage, index)
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setAdvantageToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setAdvantageToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this advantage? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditAdvantages;
